import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSpinner, faFileExcel, faSearch, faCheckCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import apiService from '../services/apiService';
import { useToast } from '../contexts/ToastContext';
import './styles/CustomizedVariable.css';

/**
 * OE (open-end) coded-data import modal.
 *
 * Upload a workbook (a "Data" sheet of coded respondent answers + one code-frame
 * sheet per question), map its ID column to an existing SPSS variable, preview
 * what will be created/recoded/appended, then commit. Mirrors DeleteIdsModal's
 * preview-then-commit pattern; the backend is oe_import.py
 * (/api/oe-import/preview and /api/oe-import/commit).
 */
const OeImportModal = ({ isOpen, onClose, projectId, user, client, project, onImported }) => {
  const { toast } = useToast();

  const [step, setStep] = useState('upload'); // 'upload' | 'review'
  const [file, setFile] = useState(null);
  const [idVariable, setIdVariable] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);

  if (!isOpen) return null;

  const reset = () => {
    setStep('upload');
    setFile(null);
    setIdVariable('');
    setPreview(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const buildFormData = () => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('idVariable', idVariable.trim());
    fd.append('projectId', projectId);
    return fd;
  };

  const handlePreview = async () => {
    if (!file) { toast.warning('Please choose an Excel workbook'); return; }
    if (!idVariable.trim()) { toast.warning('Please enter the ID variable name'); return; }
    setPreviewing(true);
    try {
      const resp = await fetch(`${apiService.baseURL}/api/oe-import/preview/${projectId}`, {
        method: 'POST',
        body: buildFormData(),
      });
      const data = await resp.json();
      if (!data?.success) throw new Error(data?.error || 'Preview failed');
      setPreview(data);
      setStep('review');
      if (data.respondentsMatched === 0) {
        toast.warning('No respondents from the workbook matched the ID variable');
      }
    } catch (error) {
      toast.error(error.message || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const handleCommit = async () => {
    if (!preview) return;
    setCommitting(true);
    try {
      const fd = buildFormData();
      fd.append('user', user || '');
      fd.append('client', JSON.stringify(client || {}));
      fd.append('project', JSON.stringify(project || {}));
      const resp = await fetch(`${apiService.baseURL}/api/oe-import/commit/${projectId}`, {
        method: 'POST',
        body: fd,
      });
      const data = await resp.json();
      if (!data?.success) throw new Error(data?.error || 'Import failed');
      toast.success(
        `Imported OE data: ${data.summary.created.length} new, ${data.summary.recoded.length} recoded, `
        + `${data.summary.appended.length} appended (${data.summary.respondentsMatched} respondents).`
      );
      if (onImported) onImported(data.qgroup_json);
      handleClose();
    } catch (error) {
      toast.error(error.message || 'Import failed');
    } finally {
      setCommitting(false);
    }
  };

  return (
    <div className="modal-overlay modal-overlay--above-settings" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }}>

        <div className="modal-header">
          <h3>Import OE Data</h3>
          <button onClick={handleClose} className="modal-close-button">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {step === 'upload' && (
          <>
            <div className="variable-name-input">
              <label>Workbook:</label>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </div>

            <div className="variable-name-input">
              <label>ID Variable Name:</label>
              <input
                type="text"
                value={idVariable}
                onChange={(e) => setIdVariable(e.target.value)}
                placeholder="e.g. record / respid — the SPSS variable matching the Data sheet's ID column"
              />
            </div>

            <div style={{
              border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px',
              fontSize: 12.5, lineHeight: 1.5, marginTop: 10, background: '#f9fafb', color: '#374151',
            }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>How to format your file</div>
              <div><strong>Data</strong> sheet: column A = respondent ID (A1 = its label). Remaining
                columns, one per row 1 header = a coded SPSS variable name. Names sharing a numeric
                suffix (e.g. <code>Q1_1</code>, <code>Q1_2</code>) form a grid. One code per cell.</div>
              <div style={{ marginTop: 6 }}><strong>One sheet per code frame</strong>: row 1 lists the
                question name(s) using it (usually just cell A1); A2 = title, A3 = base title,
                A4 = base logic (optional); row 5 is a header row (ignored); from row 6, column A =
                code, column B = label.</div>
              <div style={{ marginTop: 6 }}>A label in column B with a <strong>blank</strong> column A
                starts a <strong>Net</strong> — it aggregates every coded row below it until the next
                blank-A row.</div>
              <div style={{ marginTop: 6 }}>If the question name matches an existing question in this
                project, new codes are merged in (recoded for a single-response "other, specify",
                or appended as new rows for a grid) instead of creating a new question.</div>
            </div>

            <div className="modal-footer">
              <div className="footer-buttons">
                <button className="add-row-btn" onClick={handlePreview} disabled={previewing}>
                  {previewing
                    ? <><FontAwesomeIcon icon={faSpinner} spin /> Checking…</>
                    : <><FontAwesomeIcon icon={faSearch} /> Preview import</>}
                </button>
              </div>
            </div>
          </>
        )}

        {step === 'review' && preview && (
          <>
            <div style={{ fontSize: 13, marginBottom: 10 }}>
              <FontAwesomeIcon icon={faFileExcel} /> <strong>{file?.name}</strong>
              {' — '}<strong>{preview.respondentsMatched}</strong> respondent(s) matched on "{idVariable.trim()}"
            </div>

            {preview.unmatchedIdCount > 0 && (
              <div style={{ color: '#b45309', fontSize: 12.5, marginBottom: 8 }}>
                <FontAwesomeIcon icon={faExclamationTriangle} /> {preview.unmatchedIdCount} ID(s) from the
                workbook did not match any respondent: {preview.unmatchedIds.slice(0, 10).join(', ')}
                {preview.unmatchedIdCount > 10 ? '…' : ''}
              </div>
            )}

            {preview.warnings?.length > 0 && (
              <div style={{ color: '#b45309', fontSize: 12.5, marginBottom: 8 }}>
                {preview.warnings.map((w, i) => (
                  <div key={i}><FontAwesomeIcon icon={faExclamationTriangle} /> {w}</div>
                ))}
              </div>
            )}

            {preview.created.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>New questions ({preview.created.length})</div>
                {preview.created.map((c) => (
                  <div key={c.key} style={{ fontSize: 12.5, color: '#374151', padding: '2px 0' }}>
                    <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#16a34a' }} /> <strong>{c.key}</strong>
                    {' '}({c.type}) — {c.codeCount} code(s){c.netCount ? `, ${c.netCount} net(s)` : ''}
                    {c.type === 'Single2DGrid' ? `, ${c.rowCount} row(s)` : ''}
                  </div>
                ))}
              </div>
            )}

            {preview.recoded.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>Recoded (other, specify) ({preview.recoded.length})</div>
                {preview.recoded.map((r) => (
                  <div key={r.key} style={{ fontSize: 12.5, color: '#374151', padding: '2px 0' }}>
                    <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#16a34a' }} /> <strong>{r.key}</strong>
                    {' '}— {r.newCodeCount} new code(s) added
                  </div>
                ))}
              </div>
            )}

            {preview.appended.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>Appended to grid ({preview.appended.length})</div>
                {preview.appended.map((a) => (
                  <div key={a.key} style={{ fontSize: 12.5, color: '#374151', padding: '2px 0' }}>
                    <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#16a34a' }} /> <strong>{a.key}</strong>
                    {' '}({a.type}) — {a.codeCount} code(s)
                  </div>
                ))}
              </div>
            )}

            {preview.created.length === 0 && preview.recoded.length === 0 && preview.appended.length === 0 && (
              <div style={{ color: '#b45309', fontSize: 13 }}>Nothing to import from this workbook.</div>
            )}

            <div className="modal-footer">
              <div className="footer-buttons">
                <button className="add-row-btn" onClick={() => setStep('upload')} disabled={committing}>
                  Back
                </button>
                <button
                  className="save-btn"
                  onClick={handleCommit}
                  disabled={committing || preview.respondentsMatched === 0
                    || (preview.created.length === 0 && preview.recoded.length === 0 && preview.appended.length === 0)}
                >
                  {committing
                    ? <><FontAwesomeIcon icon={faSpinner} spin /> Importing…</>
                    : <><FontAwesomeIcon icon={faCheckCircle} /> Confirm import</>}
                </button>
              </div>
            </div>
          </>
        )}

      </div>
    </div>
  );
};

export default OeImportModal;
